export type MetaPixelParams = Record<string, string | number | string[]>;

type MetaPixelOptions = {
  eventID?: string;
};

declare global {
  interface Window {
    fbq: (
      command: string,
      eventOrPixelId: string,
      eventOrParams?: string | MetaPixelParams,
      paramsOrOptions?: MetaPixelParams | MetaPixelOptions,
      options?: MetaPixelOptions,
    ) => void;
  }
}

const readyListeners = new Set<() => void>();
let readyPixelId: string | null = null;

export function validMetaPixelId(pixelId: string | null | undefined): string | null {
  const trimmed = pixelId?.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return null;
  return trimmed;
}

/** Called once our pixel id has been passed to fbq('init'). */
export function markMetaPixelReady(pixelId: string) {
  readyPixelId = pixelId;
  for (const listener of readyListeners) {
    listener();
  }
}

/**
 * Runs send once our pixel is initialized. Publisher header scripts can define
 * window.fbq first; events must wait for our init so trackSingle targets our id.
 */
export function whenMetaPixelReady(pixelId: string, send: () => void) {
  const fire = () => {
    if (readyPixelId !== pixelId || typeof window === 'undefined' || !window.fbq) return;
    send();
  };

  if (readyPixelId === pixelId) {
    fire();
    return () => {};
  }

  readyListeners.add(fire);
  return () => {
    readyListeners.delete(fire);
  };
}

export function resetMetaPixelReadyForTests() {
  readyPixelId = null;
  readyListeners.clear();
}
